import React, { useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { brandCSS } from '../../brand'
import { CATEGORY_CSS } from './category.css.js'
import Hero from './Hero'
import PostCard from './PostCard'
import BookingSearchForm from '../components/BookingSearchForm.jsx'

const PER_PAGE = 9

async function fetchMorePosts(categoryId, page, restUrl, nonce) {
  const base = restUrl.replace(/\/$/, '')
  const params = new URLSearchParams({
    per_page: PER_PAGE,
    page,
    _fields: 'id,title,excerpt,date,link,_links',
    orderby: 'date',
    order: 'desc',
  })
  if (categoryId) params.set('categories', categoryId)

  const res = await fetch(`${base}/wp/v2/posts?${params}`, {
    headers: { 'X-WP-Nonce': nonce },
  })
  if (!res.ok) throw new Error('fetch failed')

  const totalPages = parseInt(res.headers.get('X-WP-TotalPages') || '1', 10)
  const items = await res.json()

  // Fetch featured media for each post that has one
  const withThumbs = await Promise.all(items.map(async (p) => {
    const mediaId = p._links?.['wp:featuredmedia']?.[0]?.href?.match(/\/(\d+)$/)?.[1]
    let thumb = null
    if (mediaId) {
      try {
        const mr = await fetch(`${base}/wp/v2/media/${mediaId}?_fields=media_details`, {
          headers: { 'X-WP-Nonce': nonce },
        })
        if (mr.ok) {
          const m = await mr.json()
          thumb = m.media_details?.sizes?.large?.source_url
            || m.media_details?.sizes?.full?.source_url
            || null
        }
      } catch {}
    }
    return {
      id:      p.id,
      title:   p.title?.rendered ?? '',
      excerpt: p.excerpt?.rendered?.replace(/<[^>]+>/g, '').trim() ?? '',
      date:    new Date(p.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      link:    p.link,
      thumb,
    }
  }))

  return { posts: withThumbs, totalPages }
}

export default function CategoryPage({ data }) {
  const initialPosts = data?.posts        ?? []
  const title        = data?.archiveTitle ?? 'Category'
  const heroImg      = data?.blogHero     ?? null
  const totalPages   = data?.pagination?.total   ?? 1
  const categoryId   = data?.categoryId   ?? null
  const restUrl      = window.wpData?.restUrl ?? ''
  const nonce        = window.wpData?.nonce   ?? ''

  const [posts, setPosts]       = useState(initialPosts)
  const [page, setPage]         = useState(1)
  const [loading, setLoading]   = useState(false)
  const [exhausted, setExhausted] = useState(totalPages <= 1)
  const offsetRef = useRef(initialPosts.length)

  async function handleLoadMore() {
    if (loading || exhausted) return
    setLoading(true)
    try {
      const nextPage = page + 1
      const { posts: newPosts, totalPages: tp } = await fetchMorePosts(categoryId, nextPage, restUrl, nonce)
      setPosts(prev => [...prev, ...newPosts])
      setPage(nextPage)
      offsetRef.current += newPosts.length
      if (nextPage >= tp) setExhausted(true)
    } catch (e) {
      console.error('Load more failed:', e)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <style>{brandCSS + CATEGORY_CSS}</style>
      <div className="arc-page">

        <Hero title={title} heroImg={heroImg} />
        <BookingSearchForm />

        <div className="arc-main">
          {posts.length === 0 ? (
            <div className="arc-empty">No posts in this category yet.</div>
          ) : (
            <>
              <div className="arc-grid">
                {posts.map((post, i) => (
                  <PostCard key={post.id} post={post} index={i} />
                ))}
              </div>

              {!exhausted && (
                <div className="arc-loadmore">
                  <motion.button
                    className={`arc-loadmore__btn ${loading ? 'arc-loadmore__btn--loading' : ''}`}
                    onClick={handleLoadMore}
                    disabled={loading}
                    whileTap={{ scale: 0.97 }}
                  >
                    {loading ? (
                      <span className="arc-loadmore__spinner" />
                    ) : (
                      <>
                        <span>Load More</span>
                        <span className="arc-loadmore__line" />
                      </>
                    )}
                  </motion.button>
                </div>
              )}

              {exhausted && posts.length > initialPosts.length && (
                <motion.div
                  className="arc-loadmore arc-loadmore--end"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6 }}
                >
                  <span className="arc-loadmore__end-line" />
                  <span className="arc-loadmore__end-text">You've reached the end</span>
                  <span className="arc-loadmore__end-line" />
                </motion.div>
              )}
            </>
          )}
        </div>

      </div>
    </>
  )
}
